import { PROFILE_LABEL_SX } from "../../profileFieldStyles";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Button,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { TAB_LIGHT_BLUE } from "../CompanyTabHeaderShared";
import TabSectionHeader from "../../TabSectionHeader";
import { isCompanyProfileOverviewIncomplete } from "../../../utils/companyProfileOverview";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");
const PURPLE = "#9C4FFF";
const READ_ONLY_BORDER = "#D2D8DE";
const EDIT_MODE_BORDER = "#D9C2FF";
const EDIT_MODE_OUTLINE = "0 0 0 1px rgba(156,79,255,0.22)";

const getToken = () => {
  const direct =
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("accessToken") ||
    localStorage.getItem("access_token") ||
    localStorage.getItem("idToken") ||
    localStorage.getItem("id_token") ||
    sessionStorage.getItem("token") ||
    sessionStorage.getItem("authToken") ||
    sessionStorage.getItem("jwt") ||
    sessionStorage.getItem("accessToken") ||
    sessionStorage.getItem("access_token") ||
    sessionStorage.getItem("idToken") ||
    sessionStorage.getItem("id_token");

  if (direct && String(direct).trim()) return String(direct).trim();

  const tryJsonKeys = ["user", "auth", "session", "profile"];
  for (const key of tryJsonKeys) {
    try {
      const raw = localStorage.getItem(key) || sessionStorage.getItem(key);
      if (!raw) continue;
      const obj = JSON.parse(raw);
      const token =
        obj?.token ||
        obj?.accessToken ||
        obj?.access_token ||
        obj?.jwt ||
        obj?.idToken ||
        obj?.id_token;
      if (token && String(token).trim()) return String(token).trim();
    } catch {
      // ignore malformed session data
    }
  }

  return "";
};

const getAuthHeaders = () => {
  const token = getToken();
  if (!token) return {};

  const rawToken = token.replace(/^Bearer\s+/i, "");
  return {
    Authorization: token.startsWith("Bearer ") ? token : `Bearer ${token}`,
    "x-access-token": rawToken,
  };
};

const descriptionLabelStyle = PROFILE_LABEL_SX;

const labelStyle = PROFILE_LABEL_SX;

const parseStringArray = (raw) => {
  if (Array.isArray(raw)) {
    return raw.map((item) => String(item ?? "").trim()).filter(Boolean);
  }

  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item ?? "").trim()).filter(Boolean);
      }
    } catch {
      // fall back to comma-separated parsing
    }

    return trimmed
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const COMPANY_NARRATIVE_LANGUAGE_CODES = new Set([
  "ar",
  "br",
  "de",
  "en",
  "es",
  "fr",
  "it",
  "jp",
  "kr",
  "mx",
  "nl",
  "pl",
  "pt",
  "sv",
  "zh",
  "zh-cn",
  "zh-tw",
]);

const COMPANY_NARRATIVE_NOISE_LINE_REGEXES = [
  /^skip to (?:main )?content$/i,
  /^toggle menu$/i,
  /^menu$/i,
  /^home$/i,
  /^search$/i,
  /^back$/i,
  /^next$/i,
  /^products?$/i,
  /^solutions?$/i,
  /^platform$/i,
  /^partners?$/i,
  /^customer help$/i,
  /^community$/i,
  /^explore the platform$/i,
  /^what we sell$/i,
  /^find your city(?:\s*[>\u203a\u00bb])?$/i,
  /^learn more(?:\s*[>\u203a\u00bb])?$/i,
  /^contact us$/i,
  /^request (?:a )?demo$/i,
  /^sign in$/i,
  /^log in$/i,
  /^cookie (?:settings|preferences)$/i,
  /^privacy (?:policy|choices?)$/i,
  /^terms(?: of (?:service|use))?$/i,
  /^all rights reserved$/i,
];

const normalizeCompanyNarrativeLine = (line = "") =>
  String(line ?? "")
    .replace(/[ \t]+/g, " ")
    .replace(/^[\u2022*-]+\s*/, "")
    .replace(/\s*[\u203a\u00bb>]+\s*$/, "")
    .replace(/\s{2,}/g, " ")
    .trim();

const isLikelyCompanyNarrativeNoiseLine = (line = "") => {
  const normalized = normalizeCompanyNarrativeLine(line);
  if (!normalized) return true;

  const lower = normalized.toLowerCase();
  if (COMPANY_NARRATIVE_LANGUAGE_CODES.has(lower)) return true;
  if (/^(?:https?:\/\/|www\.)/i.test(normalized)) return true;
  if (/^[|/\\><~`^_=*.-]+$/.test(normalized)) return true;

  return COMPANY_NARRATIVE_NOISE_LINE_REGEXES.some((pattern) => pattern.test(normalized));
};

const isLikelyCompanyNarrativeLine = (line = "") => {
  const normalized = normalizeCompanyNarrativeLine(line);
  if (!normalized || isLikelyCompanyNarrativeNoiseLine(normalized)) return false;

  const words = normalized.split(/\s+/).filter(Boolean);
  return (
    /[.!?]/.test(normalized) ||
    normalized.length >= 45 ||
    words.length >= 6 ||
    (/[|:;\u2013\u2014-]/.test(normalized) && words.length >= 5)
  );
};

const sanitizeCompanyNarrativeText = (value, max = 5000) => {
  const normalized = String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .trim();

  if (!normalized) return "";

  const nonNoiseLines = [];
  const seen = new Set();

  normalized.split("\n").forEach((rawLine) => {
    const line = normalizeCompanyNarrativeLine(rawLine);
    if (!line || isLikelyCompanyNarrativeNoiseLine(line)) return;

    const key = line.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    nonNoiseLines.push(line);
  });

  if (!nonNoiseLines.length) return "";

  const strongNarrativeLines = nonNoiseLines.filter((line) =>
    isLikelyCompanyNarrativeLine(line)
  );
  let selectedLines =
    nonNoiseLines.length > 2 && strongNarrativeLines.length
      ? strongNarrativeLines
      : nonNoiseLines;

  if (
    selectedLines.length > 1 &&
    /^[^.!?]{0,140}\|[^.!?]{0,140}$/.test(selectedLines[0])
  ) {
    selectedLines = selectedLines.slice(1);
  }

  const cleaned = selectedLines.join("\n").trim();
  if (!cleaned) return "";
  return cleaned.length > max ? cleaned.slice(0, max).trim() : cleaned;
};

const getDescriptionFieldSx = (editable = false, borderRadius = "8px") => ({
  mt: 1,
  "& .MuiOutlinedInput-root": {
    borderRadius,
    backgroundColor: "#FFFFFF",
    boxShadow: editable ? EDIT_MODE_OUTLINE : "none",
    "& fieldset": {
      borderColor: editable ? EDIT_MODE_BORDER : READ_ONLY_BORDER,
      borderWidth: 1,
    },
    "&:hover fieldset": {
      borderColor: editable ? PURPLE : READ_ONLY_BORDER,
    },
    "&.Mui-focused fieldset": {
      borderColor: editable ? PURPLE : READ_ONLY_BORDER,
      borderWidth: 1,
    },
  },
  "& .MuiOutlinedInput-input, & .MuiOutlinedInput-inputMultiline": {
    fontFamily: "Be Vietnam Pro",
    fontSize: 13,
    fontWeight: 400,
    color: "#111827",
    lineHeight: 1.55,
  },
});

const DescriptionField = ({ label, value, editable, onChange, placeholder }) => (
  <Box mb={4}>
    <Typography sx={descriptionLabelStyle}>{label}</Typography>

    {editable ? (
      <TextField
        multiline
        minRows={3}
        maxRows={10}
        fullWidth
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        sx={getDescriptionFieldSx(true)}
      />
    ) : (
      <Box
        sx={{
          mt: 2,
          background: "#fff",
          border: `1px solid ${READ_ONLY_BORDER}`,
          borderRadius: "8px",
          p: 3,
          whiteSpace: "pre-line",
          lineHeight: "24px",
          fontFamily: "Be Vietnam Pro",
          fontSize: 14,
          color: value ? "#344054" : "#A2ABB4",
        }}
      >
        {value || placeholder || "No information added yet."}
      </Box>
    )}
  </Box>
);

const buildDescriptionInfo = ({
  elevatorPitch = "",
  fullDescription = "",
  missionVision = "",
  keyProducts = "",
  usp = "",
  perks = "",
  achievements = "",
  techStack = [],
} = {}) => ({
  elevator_pitch: sanitizeCompanyNarrativeText(elevatorPitch, 2000) || "",
  full_company_description: sanitizeCompanyNarrativeText(fullDescription, 50000) || "",
  mission_and_vision: sanitizeCompanyNarrativeText(missionVision, 5000) || "",
  key_products_services: sanitizeCompanyNarrativeText(keyProducts, 5000) || "",
  unique_selling_points: sanitizeCompanyNarrativeText(usp, 5000) || "",
  employee_perks: sanitizeCompanyNarrativeText(perks, 5000) || "",
  achievements_awards: sanitizeCompanyNarrativeText(achievements, 5000) || "",
  recruitment_tools: Array.isArray(techStack) ? techStack : parseStringArray(techStack),
});

const getDescriptionInfoFingerprint = (value = {}) =>
  JSON.stringify({
    elevator_pitch: String(value?.elevator_pitch || "").trim(),
    full_company_description: String(value?.full_company_description || "").trim(),
    mission_and_vision: String(value?.mission_and_vision || "").trim(),
    key_products_services: String(value?.key_products_services || "").trim(),
    unique_selling_points: String(value?.unique_selling_points || "").trim(),
    employee_perks: String(value?.employee_perks || "").trim(),
    achievements_awards: String(value?.achievements_awards || "").trim(),
    recruitment_tools: parseStringArray(value?.recruitment_tools),
  });

const shouldPromoteNarrativeValue = (nextValue = "", currentValue = "") => {
  const nextText = String(nextValue || "").trim();
  const currentText = String(currentValue || "").trim();
  if (!nextText || nextText === currentText) return false;
  if (!currentText) return true;
  return nextText.length > currentText.length;
};

const shouldPromoteArrayValue = (nextValue = [], currentValue = []) => {
  const nextItems = parseStringArray(nextValue);
  const currentItems = parseStringArray(currentValue);
  if (!nextItems.length) return false;
  if (!currentItems.length) return true;
  return nextItems.length > currentItems.length;
};

const buildAutoPersistDescriptionPatch = (incoming = {}, persisted = {}) => {
  const patch = {};

  if (shouldPromoteNarrativeValue(incoming?.elevator_pitch, persisted?.elevator_pitch)) {
    patch.elevator_pitch = incoming.elevator_pitch;
  }
  if (
    shouldPromoteNarrativeValue(
      incoming?.full_company_description,
      persisted?.full_company_description
    )
  ) {
    patch.full_company_description = incoming.full_company_description;
  }
  if (shouldPromoteNarrativeValue(incoming?.mission_and_vision, persisted?.mission_and_vision)) {
    patch.mission_and_vision = incoming.mission_and_vision;
  }
  if (
    shouldPromoteNarrativeValue(
      incoming?.key_products_services,
      persisted?.key_products_services
    )
  ) {
    patch.key_products_services = incoming.key_products_services;
  }
  if (
    shouldPromoteNarrativeValue(
      incoming?.unique_selling_points,
      persisted?.unique_selling_points
    )
  ) {
    patch.unique_selling_points = incoming.unique_selling_points;
  }
  if (shouldPromoteNarrativeValue(incoming?.employee_perks, persisted?.employee_perks)) {
    patch.employee_perks = incoming.employee_perks;
  }
  if (
    shouldPromoteNarrativeValue(
      incoming?.achievements_awards,
      persisted?.achievements_awards
    )
  ) {
    patch.achievements_awards = incoming.achievements_awards;
  }
  if (shouldPromoteArrayValue(incoming?.recruitment_tools, persisted?.recruitment_tools)) {
    patch.recruitment_tools = parseStringArray(incoming.recruitment_tools);
  }

  return patch;
};

const CompanyDescriptionSection = ({
  leadId,
  companyId = null,
  entityType = "lead",
  onCompanyChange,
  companyProfileEnrichmentPending = false,
  elevatorPitch: initialElevatorPitch = "Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation.",
  fullDescription: initialFullDescription = `Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation. Prepared financial models, conducted variance analysis, and generated reports to support decision-making processes. Assisted in budgeting and forecasting activities.

Prepared financial models, conducted variance analysis, and generated reports to support decision-making processes. Assisted in budgeting and forecasting activities. Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation.`,
  missionVision: initialMissionVision = "Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation. Prepared financial models, conducted variance analysis, and generated reports to support decision-making processes. Assisted in budgeting and forecasting activities.",
  keyProducts: initialKeyProducts = "Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation. Prepared financial models, conducted variance analysis, and generated reports to support decision-making processes. Assisted in budgeting and forecasting activities.",
  usp: initialUsp = "Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation. Prepared financial models, conducted variance analysis, and generated reports to support decision-making processes. Assisted in budgeting and forecasting activities.",
  perks: initialPerks = "Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation. Prepared financial models, conducted variance analysis, and generated reports to support decision-making processes. Assisted in budgeting and forecasting activities.",
  achievements: initialAchievements = "Provided financial analysis and reporting support as a financial analyst at Global Finance Corporation. Prepared financial models, conducted variance analysis, and generated reports to support decision-making processes. Assisted in budgeting and forecasting activities.",
  techStack: initialTechStack = ["LAMP", "ASP.NET", "MEAN", "MERN", "MEVN", "Ruby on Rails"],
}) => {
  const isCompanyEntity = String(entityType || "lead").toLowerCase() === "company";
  const recordId = isCompanyEntity ? companyId : leadId;
  const canEdit = Boolean(recordId);

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(Boolean(recordId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [elevatorPitch, setElevatorPitch] = useState(initialElevatorPitch);
  const [fullDescription, setFullDescription] = useState(initialFullDescription);
  const [missionVision, setMissionVision] = useState(initialMissionVision);
  const [keyProducts, setKeyProducts] = useState(initialKeyProducts);
  const [uspValue, setUspValue] = useState(initialUsp);
  const [perksValue, setPerksValue] = useState(initialPerks);
  const [achievementsValue, setAchievementsValue] = useState(initialAchievements);
  const [techStackState, setTechStackState] = useState(initialTechStack);

  const [isEditing, setIsEditing] = useState(false);
  const [newTechItem, setNewTechItem] = useState("");
  const hasLoadedPersistedRecordRef = useRef(false);
  const persistedDescriptionInfoRef = useRef(buildDescriptionInfo());
  const persistedInfoFingerprintRef = useRef("");
  const autoPersistInFlightRef = useRef(false);

  const incomingDescriptionInfo = useMemo(
    () =>
      buildDescriptionInfo({
        elevatorPitch: initialElevatorPitch,
        fullDescription: initialFullDescription,
        missionVision: initialMissionVision,
        keyProducts: initialKeyProducts,
        usp: initialUsp,
        perks: initialPerks,
        achievements: initialAchievements,
        techStack: initialTechStack,
      }),
    [
      initialAchievements,
      initialElevatorPitch,
      initialFullDescription,
      initialKeyProducts,
      initialMissionVision,
      initialPerks,
      initialTechStack,
      initialUsp,
    ]
  );

  const syncStateFromInfo = useCallback((nextInfo = {}) => {
    const normalizedInfo = buildDescriptionInfo({
      elevatorPitch: nextInfo?.elevator_pitch,
      fullDescription:
        nextInfo?.full_company_description || nextInfo?.company_description,
      missionVision: nextInfo?.mission_and_vision,
      keyProducts: nextInfo?.key_products_services,
      usp: nextInfo?.unique_selling_points,
      perks: nextInfo?.employee_perks,
      achievements: nextInfo?.achievements_awards,
      techStack: nextInfo?.recruitment_tools,
    });

    setInfo({ ...(nextInfo || {}), ...normalizedInfo });
    setElevatorPitch(normalizedInfo.elevator_pitch);
    setFullDescription(normalizedInfo.full_company_description);
    setMissionVision(normalizedInfo.mission_and_vision);
    setKeyProducts(normalizedInfo.key_products_services);
    setUspValue(normalizedInfo.unique_selling_points);
    setPerksValue(normalizedInfo.employee_perks);
    setAchievementsValue(normalizedInfo.achievements_awards);
    setTechStackState(parseStringArray(normalizedInfo.recruitment_tools));
  }, []);

  useEffect(() => {
    if (recordId) return;

    setInfo(null);
    setLoading(false);
    setSaving(false);
    setError("");
    setIsEditing(false);
    hasLoadedPersistedRecordRef.current = false;
    persistedDescriptionInfoRef.current = buildDescriptionInfo();
    persistedInfoFingerprintRef.current = "";
    autoPersistInFlightRef.current = false;
    syncStateFromInfo(incomingDescriptionInfo);
  }, [
    incomingDescriptionInfo,
    recordId,
    syncStateFromInfo,
  ]);

  useEffect(() => {
    if (!isCompanyEntity || !recordId || isEditing) return undefined;

    syncStateFromInfo(incomingDescriptionInfo);
    setLoading(false);
    setError("");

    return undefined;
  }, [
    incomingDescriptionInfo,
    isCompanyEntity,
    recordId,
    isEditing,
    syncStateFromInfo,
  ]);

  useEffect(() => {
    // LeadTabsLayout already loads the full company record and refreshes it
    // while background enrichment is pending. A second /info request here
    // made saved overview fields visibly arrive after the profile opened.
    // Keep this fetch for lead records, whose parent payload is intentionally
    // smaller, but render company data directly from that shared detail state.
    if (!recordId || isCompanyEntity) return undefined;

    const controller = new AbortController();
    let active = true;

    setLoading(true);
    setSaving(false);
    setError("");
    setIsEditing(false);

    const infoUrl = isCompanyEntity
      ? `${API_BASE}/api/companies/${recordId}/info`
      : `${API_BASE}/api/leads/${recordId}/info`;

    fetch(infoUrl, {
      signal: controller.signal,
      headers: {
        ...getAuthHeaders(),
      },
      credentials: "include",
    })
      .then(async (response) => {
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload?.error || "Failed to load company info");
        }
        return response.json();
      })
      .then((data) => {
        if (!active) return;

        const nextInfo =
          isCompanyEntity && data?.company && typeof data.company === "object"
            ? data.company
            : data || {};
        const normalizedNextInfo = buildDescriptionInfo({
          elevatorPitch: nextInfo?.elevator_pitch,
          fullDescription:
            nextInfo?.full_company_description || nextInfo?.company_description,
          missionVision: nextInfo?.mission_and_vision,
          keyProducts: nextInfo?.key_products_services,
          usp: nextInfo?.unique_selling_points,
          perks: nextInfo?.employee_perks,
          achievements: nextInfo?.achievements_awards,
          techStack: nextInfo?.recruitment_tools,
        });
        persistedDescriptionInfoRef.current = normalizedNextInfo;
        persistedInfoFingerprintRef.current = getDescriptionInfoFingerprint(normalizedNextInfo);
        hasLoadedPersistedRecordRef.current = true;
        syncStateFromInfo(nextInfo);
      })
      .catch((fetchError) => {
        if (!active || fetchError?.name === "AbortError") return;
        console.error(fetchError);
        setError(fetchError.message || "Failed to load company info");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [recordId, isCompanyEntity, syncStateFromInfo]);

  useEffect(() => {
    if (!recordId || isEditing || loading || saving) return undefined;
    if (!hasLoadedPersistedRecordRef.current || autoPersistInFlightRef.current) return undefined;

    const persistedInfo = persistedDescriptionInfoRef.current || buildDescriptionInfo();
    const patch = buildAutoPersistDescriptionPatch(incomingDescriptionInfo, persistedInfo);
    if (!Object.keys(patch).length) return undefined;

    const nextFingerprint = getDescriptionInfoFingerprint({
      ...persistedInfo,
      ...patch,
    });
    if (nextFingerprint === persistedInfoFingerprintRef.current) return undefined;

    const controller = new AbortController();
    let active = true;
    autoPersistInFlightRef.current = true;

    const infoUrl = isCompanyEntity
      ? `${API_BASE}/api/companies/${recordId}/info`
      : `${API_BASE}/api/leads/${recordId}/info`;

    fetch(infoUrl, {
      method: "PUT",
      signal: controller.signal,
      headers: {
        "Content-Type": "application/json",
        ...getAuthHeaders(),
      },
      credentials: isCompanyEntity ? "include" : "same-origin",
      body: JSON.stringify(patch),
    })
      .then(async (response) => {
        const payload = await response.json().catch(() => ({}));
        if (!response.ok) {
          throw new Error(payload?.error || "Failed to save AI-generated company description");
        }
        return payload;
      })
      .then((payload) => {
        if (!active) return;

        const nextInfo =
          isCompanyEntity && payload?.company && typeof payload.company === "object"
            ? payload.company
            : payload?.lead && typeof payload.lead === "object"
              ? payload.lead
              : { ...persistedInfo, ...patch };
        const normalizedNextInfo = buildDescriptionInfo({
          elevatorPitch: nextInfo?.elevator_pitch,
          fullDescription:
            nextInfo?.full_company_description || nextInfo?.company_description,
          missionVision: nextInfo?.mission_and_vision,
          keyProducts: nextInfo?.key_products_services,
          usp: nextInfo?.unique_selling_points,
          perks: nextInfo?.employee_perks,
          achievements: nextInfo?.achievements_awards,
          techStack: nextInfo?.recruitment_tools,
        });
        persistedDescriptionInfoRef.current = normalizedNextInfo;
        persistedInfoFingerprintRef.current = getDescriptionInfoFingerprint(normalizedNextInfo);
        syncStateFromInfo(nextInfo);
        onCompanyChange?.({
          ...(nextInfo || {}),
          entityType: isCompanyEntity ? "company" : "lead",
        });
      })
      .catch((persistError) => {
        if (!active || persistError?.name === "AbortError") return;
        console.error("Failed to auto-save AI company description:", persistError);
      })
      .finally(() => {
        if (active) {
          autoPersistInFlightRef.current = false;
        }
      });

    return () => {
      active = false;
      autoPersistInFlightRef.current = false;
      controller.abort();
    };
  }, [
    incomingDescriptionInfo,
    isCompanyEntity,
    isEditing,
    loading,
    onCompanyChange,
    recordId,
    saving,
    syncStateFromInfo,
  ]);

  const handleAddTechItem = () => {
    const value = newTechItem.trim();
    if (!value) return;
    if (!techStackState.includes(value)) {
      setTechStackState((prev) => [...prev, value]);
    }
    setNewTechItem("");
  };

  const handleRemoveTechItem = (item) => {
    setTechStackState((prev) => prev.filter((value) => value !== item));
  };

  const handleStartEdit = () => {
    if (!canEdit) return;
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (!canEdit || !recordId) return;

    setSaving(true);
    setError("");

    const payload = buildDescriptionInfo({
      elevatorPitch,
      fullDescription,
      missionVision,
      keyProducts,
      usp: uspValue,
      perks: perksValue,
      achievements: achievementsValue,
      techStack: techStackState,
    });

    try {
      const response = await fetch(
        isCompanyEntity
          ? `${API_BASE}/api/companies/${recordId}/info`
          : `${API_BASE}/api/leads/${recordId}/info`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...(isCompanyEntity ? getAuthHeaders() : {}),
          },
          credentials: isCompanyEntity ? "include" : "same-origin",
          body: JSON.stringify(payload),
        }
      );

      const responseBody = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseBody?.error || "Failed to save company info");
      }

      const nextInfo =
        isCompanyEntity && responseBody?.company && typeof responseBody.company === "object"
          ? responseBody.company
          : { ...(info || {}), ...payload };

      const normalizedNextInfo = {
        ...(nextInfo || {}),
        ...buildDescriptionInfo({
          elevatorPitch: nextInfo?.elevator_pitch,
          fullDescription:
            nextInfo?.full_company_description || nextInfo?.company_description,
          missionVision: nextInfo?.mission_and_vision,
          keyProducts: nextInfo?.key_products_services,
          usp: nextInfo?.unique_selling_points,
          perks: nextInfo?.employee_perks,
          achievements: nextInfo?.achievements_awards,
          techStack: nextInfo?.recruitment_tools,
        }),
      };

      persistedDescriptionInfoRef.current = normalizedNextInfo;
      persistedInfoFingerprintRef.current = getDescriptionInfoFingerprint(normalizedNextInfo);
      hasLoadedPersistedRecordRef.current = true;
      syncStateFromInfo(normalizedNextInfo);
      setIsEditing(false);
      onCompanyChange?.({
        ...(normalizedNextInfo || {}),
        entityType: isCompanyEntity ? "company" : "lead",
      });
    } catch (saveError) {
      console.error(saveError);
      setError(saveError.message || "Failed to save company info");
    } finally {
      setSaving(false);
    }
  };

  const editable = isEditing && canEdit;
  const currentDescriptionInfo = useMemo(
    () =>
      buildDescriptionInfo({
        elevatorPitch,
        fullDescription,
        missionVision,
        keyProducts,
        usp: uspValue,
        perks: perksValue,
        achievements: achievementsValue,
      }),
    [
      achievementsValue,
      elevatorPitch,
      fullDescription,
      keyProducts,
      missionVision,
      perksValue,
      uspValue,
    ]
  );
  const showCompanyProfileEnrichmentNotice = Boolean(
    (companyProfileEnrichmentPending || info?.company_profile_enrichment_pending) &&
      isCompanyProfileOverviewIncomplete(currentDescriptionInfo)
  );

  return (
    <Card sx={{ mb: 4, borderRadius: 0, boxShadow: "none" }}>
      <TabSectionHeader
        title="Company Description/Overview"
        rightText={canEdit ? (saving ? "Saving..." : editable ? "Save" : "Edit") : ""}
        onAction={editable ? handleSave : handleStartEdit}
        actionDisabled={!canEdit || loading || saving}
        dividerColor={TAB_LIGHT_BLUE}
      />

      <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
        {showCompanyProfileEnrichmentNotice && (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 1.25,
              border: "1px solid #DDD6FE",
              borderRadius: "8px",
              backgroundColor: "#F5F3FF",
            }}
          >
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "#5B21B6" }}>
              AI is filling in the missing company overview fields in the background.
            </Typography>
            <Typography sx={{ mt: 0.35, fontSize: 12, color: "#6B7280", lineHeight: 1.45 }}>
              The company description, mission, products, and other profile details will appear
              here automatically as soon as the enrichment finishes.
            </Typography>
          </Box>
        )}

        {loading && (
          <Typography variant="body2" color="text.secondary" mb={2}>
            Loading company info...
          </Typography>
        )}

        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 2, display: "block" }}
          >
            {error}
          </Typography>
        )}

        <DescriptionField
          label="One-line Elevator Pitch:"
          value={elevatorPitch}
          editable={editable}
          onChange={setElevatorPitch}
          placeholder="Add a short one-line elevator pitch for this company."
        />

        <DescriptionField
          label="Full Company Description:"
          value={fullDescription}
          editable={editable}
          onChange={setFullDescription}
          placeholder="Add a complete description of the company, products and business model."
        />

        <DescriptionField
          label="Mission and Vision:"
          value={missionVision}
          editable={editable}
          onChange={setMissionVision}
          placeholder="Describe the mission and vision for this company."
        />

        <DescriptionField
          label="Key Products/Services Offered:"
          value={keyProducts}
          editable={editable}
          onChange={setKeyProducts}
          placeholder="List the main products or services offered."
        />

        <DescriptionField
          label="Unique Selling Points (USP):"
          value={uspValue}
          editable={editable}
          onChange={setUspValue}
          placeholder="What differentiates this company from competitors?"
        />

        <DescriptionField
          label="Employee Perks / Benefits Highlights:"
          value={perksValue}
          editable={editable}
          onChange={setPerksValue}
          placeholder="Summarize key perks and benefits."
        />

        <DescriptionField
          label="Achievements and Awards:"
          value={achievementsValue}
          editable={editable}
          onChange={setAchievementsValue}
          placeholder="Highlight major achievements, milestones or awards."
        />

        <Box mt={3}>
          <Typography sx={labelStyle}>Technology Stack:</Typography>

          <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
            {techStackState.map((stack) => (
              <Chip
                key={stack}
                label={stack}
                onDelete={editable ? () => handleRemoveTechItem(stack) : undefined}
                deleteIcon={editable ? <CloseIcon sx={{ fontSize: 16 }} /> : undefined}
                sx={{
                  background: "#EEF2FF",
                  color: "#4F46E5",
                  fontWeight: 600,
                  borderRadius: "999px",
                }}
              />
            ))}

            {!techStackState.length && !editable && (
              <Typography variant="body2" color="text.secondary">
                No technologies added yet.
              </Typography>
            )}
          </Box>

          {editable && (
            <Box mt={1} display="flex" alignItems="center" gap={1}>
              <TextField
                size="small"
                placeholder="Add a technology and press Enter"
                value={newTechItem}
                onChange={(event) => setNewTechItem(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    handleAddTechItem();
                  }
                }}
                sx={{ maxWidth: 260, ...getDescriptionFieldSx(true, "999px") }}
              />
              <Button
                variant="text"
                sx={{ p: 0, textTransform: "none", color: "#4F46E5" }}
                onClick={handleAddTechItem}
              >
                Add New
              </Button>
            </Box>
          )}

          {!editable && (
            <Button
              variant="text"
              sx={{
                mt: 1,
                p: 0,
                textTransform: "none",
                color: canEdit ? "#4F46E5" : "#A0AEC0",
              }}
              disabled={!canEdit}
              onClick={canEdit ? handleStartEdit : undefined}
            >
              Add New
            </Button>
          )}
        </Box>

      </CardContent>
    </Card>
  );
};

export default CompanyDescriptionSection;
